import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  Close as CloseIcon,
  FileCopy as CopyIcon,
  Check as CheckIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/api/iot'

const useStyles = makeStyles(theme => ({
  dialog: {
    minWidth: 500
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  activationCode: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  copyButton: {
    marginLeft: theme.spacing(1)
  },
  successIcon: {
    color: theme.palette.success.main
  }
}))

const DEVICE_TYPES = [
  { value: 'magnet_monitor', label: 'Magnet Monitor' },
  { value: 'temperature_sensor', label: 'Temperature Sensor' },
  { value: 'pressure_sensor', label: 'Pressure Sensor' },
  { value: 'flow_sensor', label: 'Flow Sensor' }
]

const DEFAULT_CAPABILITIES = [
  'HLevel', 'HPress', 'Temperature', 'RH', 'WFlow', 'WTemp', 'comp'
]

export const IoTDeviceRegistrationModal = ({ open, onClose, modalityId, onDeviceRegistered }) => {
  const classes = useStyles()
  const [formData, setFormData] = useState({
    deviceType: 'magnet_monitor',
    location: '',
    description: '',
    capabilities: DEFAULT_CAPABILITIES,
    devicePin: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [registrationResult, setRegistrationResult] = useState(null)
  const [copied, setCopied] = useState(false)

  const handleInputChange = (field) => (event) => {
    let value = event.target.value
    
    // Special handling for devicePin - convert to uppercase and limit to alphanumeric
    if (field === 'devicePin') {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCapabilityChange = (event) => {
    setFormData(prev => ({
      ...prev,
      capabilities: event.target.value
    }))
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setRegistrationResult(null)

    try {
      // Prepare data for submission, only include devicePin if it's provided
      const submitData = { ...formData }
      if (!submitData.devicePin.trim()) {
        delete submitData.devicePin
      }
      
      const result = await iotAPI.registerIoTDevice(modalityId, submitData)
      setRegistrationResult(result)
      
      // Call the callback to refresh the device list
      if (onDeviceRegistered) {
        onDeviceRegistered()
      }
    } catch (err) {
      console.error('Error registering IoT device:', err)
      setError(err.message || 'Failed to register IoT device')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      deviceType: 'magnet_monitor',
      location: '',
      description: '',
      capabilities: DEFAULT_CAPABILITIES,
      devicePin: ''
    })
    setError(null)
    setRegistrationResult(null)
    setCopied(false)
    onClose()
  }

  const copyActivationCode = async () => {
    try {
      await navigator.clipboard.writeText(registrationResult.activationCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy activation code:', err)
    }
  }

  const isFormValid = formData.deviceType && formData.location && formData.description && 
    (!formData.devicePin || formData.devicePin.length === 6)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {registrationResult ? 'Device Registered Successfully' : 'Register New IoT Device'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {registrationResult ? (
          <Box>
            <Alert severity="success" style={{ marginBottom: 16 }}>
              IoT device has been registered successfully!
            </Alert>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Device ID: <strong>{registrationResult.deviceId}</strong>
            </Typography>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Activation Code Expires: <strong>{new Date(registrationResult.expiresAt).toLocaleString()}</strong>
            </Typography>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle2" gutterBottom>
              Activation Code:
            </Typography>
            <Box className={classes.activationCode}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" style={{ fontFamily: 'monospace', flex: 1 }}>
                  {registrationResult.activationCode}
                </Typography>
                <IconButton
                  onClick={copyActivationCode}
                  size="small"
                  className={classes.copyButton}
                >
                  {copied ? (
                    <CheckIcon className={classes.successIcon} />
                  ) : (
                    <CopyIcon />
                  )}
                </IconButton>
              </Box>
            </Box>

            {registrationResult.devicePin && (
              <>
                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                  Device PIN:
                </Typography>
                <Box className={classes.activationCode}>
                  <Typography variant="h6" style={{ fontFamily: 'monospace' }}>
                    {registrationResult.devicePin}
                  </Typography>
                </Box>
              </>
            )}

            <Alert severity="info" style={{ marginTop: 16 }}>
              <Typography variant="body2">
                <strong>Next Steps:</strong><br />
                1. Enter {registrationResult.devicePin ? 'either the activation code or device PIN' : 'the activation code'} on your IoT device<br />
                2. The device will automatically activate and start sending data<br />
                3. The activation code expires in 24 hours
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={formData.deviceType}
                onChange={handleInputChange('deviceType')}
              >
                {DEVICE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={handleInputChange('location')}
              placeholder="e.g., MRI Room 1, Scanner Bay A"
              className={classes.formField}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Brief description of the device"
              className={classes.formField}
              multiline
              rows={2}
              required
            />

            <TextField
              fullWidth
              label="Device PIN (Optional)"
              value={formData.devicePin}
              onChange={handleInputChange('devicePin')}
              placeholder="6-character alphanumeric PIN (e.g., ABC123)"
              className={classes.formField}
              inputProps={{
                maxLength: 6,
                style: { textTransform: 'uppercase' }
              }}
              error={formData.devicePin && formData.devicePin.length !== 6}
              helperText={
                formData.devicePin && formData.devicePin.length !== 6
                  ? "Device PIN must be exactly 6 characters"
                  : "Leave empty to use auto-generated activation code only"
              }
            />

            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Capabilities</InputLabel>
              <Select
                multiple
                value={formData.capabilities}
                onChange={handleCapabilityChange}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {DEFAULT_CAPABILITIES.map((capability) => (
                  <MenuItem key={capability} value={capability}>
                    {capability}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {registrationResult ? (
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!isFormValid || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Registering...' : 'Register Device'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default IoTDeviceRegistrationModal
